import * as moment from "moment";
import * as React from "react";
import app from "../../core/application";
import BackButton from "../../core/BackButton/BackButton";
import * as Constants from "../../core/constants";
import ExportButton from "../../core/ExportButton/ExportButton";
import Spinner from "../../core/Spinner/Spinner";
import TableCheckboxListFilter, { ICheckboxValue } from "../../core/TableCheckboxListFilter/TableCheckboxListFilter";
import TableDateRangeFilter from "../../core/TableDateRangeFilter/TableDateRangeFilter";
import TableTextFilter from "../../core/TableTextFilter/TableTextFilter";
import debounce from "../../helpers/debounce";
import { sessionGetItem, sessionSetItem } from "../../helpers/storage";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IListResults from "../../types/IListResults";
import IResponse from "../../types/IResponse";
import IStatusChangePostLog from "../../types/IStatusChangePostLog";
import IStatusChangePostLogSearchRequest from "../../types/IStatusChangePostLogSearchRequest";
import PostbackLogDetailModal from "./PostbackLogDetailModal";
import "./PostbackLogList.css";
import PostbackLogRow from "./PostbackLogRow";

interface IPostbackLogListPersistedState {
    startDate: string;
    endDate: string;
    uniqueID: string;
    referenceID: string;
    actions: string[];
    messageTypes: string[];
}

interface IPostbackLogListProps extends ITranslationProps {
    loading: boolean;
    search: (request: IStatusChangePostLogSearchRequest) => Promise<IResponse<IListResults<IStatusChangePostLog>> | null>;
}

interface IPostbackLogListState {
    startDate: Date;
    endDate: Date;
    uniqueID: string;
    referenceID: string;
    actions: string[];
    messageTypes: string[];
    page: number;
    moreRecordsExist: boolean;
    postbackLogs: IStatusChangePostLog[];
    showDetailModal: boolean;
    selectedLog: IStatusChangePostLog | null;
}

class PostbackLogList extends React.Component<IPostbackLogListProps, IPostbackLogListState> {
    private exportForm: HTMLFormElement | null;
    private stateStorageKey: string = "postbackLogList_state_storage";
    private defaultStartDate: Date = moment().hour(0).minute(0).second(0).millisecond(0).add(-1, "month").toDate();
    private defaultEndDate: Date = moment().hour(0).minute(0).second(0).millisecond(0).toDate();
    private searchWithDebounce = debounce(() => { this.search(); }, 500);
    private messageTypeValues: ICheckboxValue[] = [
        { key: Constants.MESSAGE_TYPE_EMAIL, value: Constants.MESSAGE_TYPE_EMAIL },
        { key: Constants.MESSAGE_TYPE_PUSH, value: Constants.MESSAGE_TYPE_PUSH },
        { key: Constants.MESSAGE_TYPE_SMS, value: Constants.MESSAGE_TYPE_SMS },
        { key: Constants.MESSAGE_TYPE_VOICE, value: Constants.MESSAGE_TYPE_VOICE },
    ];
    private actionValues: ICheckboxValue[] = [
        { key: Constants.POSTBACKACTION_TYPE_BOUNCE, value: Constants.POSTBACKACTION_TYPE_BOUNCE },
        { key: Constants.POSTBACKACTION_TYPE_DELIVERYRECEIPT, value: Constants.POSTBACKACTION_TYPE_DELIVERYRECEIPT },
        { key: Constants.POSTBACKACTION_TYPE_ERROR, value: Constants.POSTBACKACTION_TYPE_ERROR },
        { key: Constants.POSTBACKACTION_TYPE_INCOMING, value: Constants.POSTBACKACTION_TYPE_INCOMING },
        { key: Constants.POSTBACKACTION_TYPE_INVALIDPHONE, value: Constants.POSTBACKACTION_TYPE_INVALIDPHONE },
        { key: Constants.POSTBACKACTION_TYPE_INVALIDREGISTRATION, value: Constants.POSTBACKACTION_TYPE_INVALIDREGISTRATION },
        { key: Constants.POSTBACKACTION_TYPE_NORESPONSE, value: Constants.POSTBACKACTION_TYPE_NORESPONSE },
        { key: Constants.POSTBACKACTION_TYPE_RESPONSE, value: Constants.POSTBACKACTION_TYPE_RESPONSE },
        { key: Constants.POSTBACKACTION_TYPE_RESPONSERECEIVED, value: Constants.POSTBACKACTION_TYPE_RESPONSERECEIVED },
        { key: Constants.POSTBACKACTION_TYPE_SENT, value: Constants.POSTBACKACTION_TYPE_SENT },
        { key: Constants.POSTBACKACTION_TYPE_TEST, value: Constants.POSTBACKACTION_TYPE_TEST },
    ];

    constructor(props: IPostbackLogListProps) {
        super(props);

        this.state = {
            actions: [],
            startDate: this.defaultStartDate,
            endDate: this.defaultEndDate,
            messageTypes: [],
            page: 1,
            referenceID: "",
            uniqueID: "",
            moreRecordsExist: false,
            postbackLogs: [],
            showDetailModal: false,
            selectedLog: null
        };

        this.search = this.search.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.updateDateRange = this.updateDateRange.bind(this);
        this.updateMessageTypes = this.updateMessageTypes.bind(this);
        this.updateUniqueID = this.updateUniqueID.bind(this);
        this.updateReferenceID = this.updateReferenceID.bind(this);
        this.updateActions = this.updateActions.bind(this);
        this.getSearchRequest = this.getSearchRequest.bind(this);
        this.onRowClick = this.onRowClick.bind(this);
        this.onModalRequestClose = this.onModalRequestClose.bind(this);
        this.onExportClick = this.onExportClick.bind(this);
    }

    public componentDidMount() {

        const savedState = sessionGetItem<IPostbackLogListPersistedState>(this.stateStorageKey);

        if (savedState) {
            this.setState({
                referenceID: savedState.referenceID,
                uniqueID: savedState.uniqueID,
                actions: savedState.actions,
                messageTypes: savedState.messageTypes,
                startDate: moment(savedState.startDate).toDate(),
                endDate: moment(savedState.endDate).toDate()
            }, () => { this.search(true) });
        } else {
            this.search(true);
        }
    }

    public componentWillUnmount() {

        const persistedState: IPostbackLogListPersistedState = {
            actions: this.state.actions,
            referenceID: this.state.referenceID,
            uniqueID: this.state.uniqueID,
            messageTypes: this.state.messageTypes,
            startDate: this.state.startDate.toJSON(),
            endDate: this.state.endDate.toJSON()
        };

        sessionSetItem(this.stateStorageKey, persistedState);
    }

    public render() {
        const application = app();
        const dlToken = application.userSession ? application.userSession.fileDownloadAuthenticationToken : "";

        const rows: React.ReactNode[] = [];
        this.state.postbackLogs.forEach((log, index) => {
            rows.push(<PostbackLogRow key={index} log={log} onRowClick={this.onRowClick} />)
        });

        const messageTypeInputs: React.ReactNode[] = [];
        this.state.messageTypes.forEach((messageType, index) => {
            messageTypeInputs.push(<input key={index} type="hidden" name="messageTypes[]" value={messageType} />);
        });

        const actionInputs: React.ReactNode[] = [];
        this.state.actions.forEach((action, index) => {
            messageTypeInputs.push(<input key={index} type="hidden" name="actions[]" value={action} />);
        });

        return (
            <div>
                {this.props.loading && <Spinner />}

                <BackButton />
                <h2>{this.props.t("PostbackLogList.Title")}</h2>

                <div className="float-right">
                    <ExportButton onClick={this.onExportClick} />
                    <form ref={ref => this.exportForm = ref} method="POST" style={{ "display": "none" }}
                        action={`${application.webServiceBaseURL}/api/messages/exportstatuschangepostlogs?dltoken=${dlToken}`}
                        target="_blank">
                        <input type="hidden" name="ReferenceID" value={this.state.referenceID} />
                        <input type="hidden" name="UniqueID" value={this.state.uniqueID} />
                        <input type="hidden" name="StartDate" value={this.state.startDate.toJSON()} />
                        <input type="hidden" name="EndDate" value={this.state.endDate.toJSON()} />
                        {messageTypeInputs}
                        {actionInputs}
                    </form>
                </div>

                <table className="table table-sm postbackloglist-table">
                    <thead className="thead-light">
                        <tr>
                            <th>
                                <div>{this.props.t("MessageList.ReferenceID")}</div>
                                <TableTextFilter onChange={this.updateReferenceID} searchButtonClick={this.search} value={this.state.referenceID} />
                            </th>
                            <th>
                                <div>{this.props.t("MessageDetail.UniqueID")}</div>
                                <TableTextFilter onChange={this.updateUniqueID} searchButtonClick={this.search} value={this.state.uniqueID} />
                            </th>
                            <th style={{ width: "315px" }}>
                                <div>{this.props.t("Global.Date")}</div>
                                <TableDateRangeFilter small={true} startDate={this.state.startDate} endDate={this.state.endDate} onChange={this.updateDateRange} />
                            </th>
                            <th>
                                <div>{this.props.t("PostbackLogList.Action")}</div>
                                <TableCheckboxListFilter values={this.actionValues} onChange={this.updateActions} selectedValues={this.state.actions} />
                            </th>
                            <th>
                                <div>{this.props.t("MessageList.MessageType")}</div>
                                <TableCheckboxListFilter values={this.messageTypeValues} onChange={this.updateMessageTypes} selectedValues={this.state.messageTypes} />
                            </th>
                        </tr>
                    </thead>
                </table>
                <table className="table table-sm postbackloglist-table table-hover">
                    <tbody>
                        <tr>
                            <th>{this.props.t("MessageList.ReferenceID")}</th>
                            <th>{this.props.t("MessageDetail.UniqueID")}</th>
                            <th>{this.props.t("Global.Date")}</th>
                            <th>{this.props.t("PostbackLogList.Action")}</th>
                            <th>{this.props.t("Global.Type")}</th>
                        </tr>
                        {rows}
                    </tbody>
                </table>
                {
                    this.state.moreRecordsExist &&
                    <button data-id="showMoreButton" className="btn btn-outline-secondary" onClick={this.loadNextPage}>{this.props.t("Global.ShowMore")}
                    </button>
                }

                <PostbackLogDetailModal isOpen={this.state.showDetailModal} log={this.state.selectedLog} onRequestClose={this.onModalRequestClose} />

            </div>
        );
    }
    private loadNextPage() {
        this.setState({ page: this.state.page + 1 }, () => { this.search(false) });
    }

    private search(newSearch: boolean = true) {

        this.setState({ page: newSearch ? 1 : this.state.page }, async () => {
            const response = await this.props.search(this.getSearchRequest());

            if (response && response.success) {
                if (newSearch) {
                    this.setState({ postbackLogs: response.payload.records, moreRecordsExist: response.payload.moreRecordsExist });
                } else {
                    this.setState((state: IPostbackLogListState) => {
                        state.postbackLogs.push(...response.payload.records);
                        state.moreRecordsExist = response.payload.moreRecordsExist;
                        return state;
                    });
                }
            }
        });
    }

    private updateReferenceID(value: string) {
        this.setState({ referenceID: value }, this.searchWithDebounce);
    }

    private updateUniqueID(value: string) {
        this.setState({ uniqueID: value }, this.searchWithDebounce);
    }

    private updateActions(selectedItems: string[]) {
        this.setState({ actions: selectedItems }, this.searchWithDebounce);
    }

    private updateMessageTypes(selectedItems: string[]) {
        this.setState({ messageTypes: selectedItems }, this.searchWithDebounce);
    }

    private updateDateRange(newStartDate: Date | null, newEndDate: Date | null) {

        const startDate = newStartDate ? newStartDate : this.defaultStartDate;
        const endDate = newEndDate ? newEndDate : this.defaultEndDate;

        if (startDate !== this.state.startDate || endDate !== this.state.endDate) {
            this.setState({ startDate, endDate, page: 1 }, this.searchWithDebounce);
        }
    }

    private onRowClick(row: IStatusChangePostLog) {
        this.setState({ selectedLog: row, showDetailModal: true });
    }

    private onModalRequestClose() {
        this.setState({ selectedLog: null, showDetailModal: false });
    }

    private getSearchRequest(): IStatusChangePostLogSearchRequest {
        return {
            referenceID: this.state.referenceID,
            uniqueID: this.state.uniqueID,
            actions: this.state.actions,
            messageTypes: this.state.messageTypes,
            page: this.state.page,
            startDate: this.state.startDate.toJSON(),
            endDate: this.state.endDate.toJSON()
        };
    }
    private onExportClick() {
        if (this.exportForm) {
            this.exportForm.submit();
        }
    }
}

export default translateComponent(PostbackLogList);



// WEBPACK FOOTER //
// ./src/components/PostbackLogList/PostbackLogList.tsx